import { 
  biologicalEntityService, 
  organizationService, 
  locationService, 
  buildingService, 
  floorService, 
  departmentService, 
  roomService, 
  bedService, 
  equipmentService, 
  personnelService,
  userService 
} from './firebaseService';

/**
 * Production-Optimized Clinical Persistence Service
 * Uses offline-first approach and aggressive caching for production
 */
export class ProductionOptimizedService {
  constructor() {
    this.isInitialized = false;
    this.cache = new Map();
    this.listeners = new Map();
    this.loadingStates = new Map();
    this.offlineData = null;
    this.lastSyncTime = null;
    this.isOnline = navigator.onLine;
    this.localStorageDisabled = false; // Track if localStorage is disabled due to quota
    
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.syncWhenOnline();
    });
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  // Ultra-fast initialization - load from cache first, then sync in background
  async initializeUltraFast(isAuthenticated = false) {
    try {
      console.log('⚡ Ultra-fast initialization...');
      
      // 1. Try to load from localStorage cache first (instant)
      const cachedData = this.loadFromLocalStorage();
      if (cachedData && this.isCacheValid(cachedData)) {
        console.log('✅ Loaded from cache (instant)');
        this.isInitialized = true;
        this.offlineData = cachedData;
        
        // Sync in background only if authenticated
        if (isAuthenticated) {
          this.syncInBackground();
        }
        return cachedData;
      }
      
      // 2. If no cache and authenticated, load minimal essential data
      if (isAuthenticated) {
        console.log('🔄 Loading minimal essential data...');
        const essentialData = await this.loadMinimalEssential();
        
        // Save to cache for next time
        this.saveToLocalStorage(essentialData);
        
        this.isInitialized = true;
        this.offlineData = essentialData;
        
        // Sync remaining data in background
        this.syncInBackground();
        
        return essentialData;
      } else {
        // 3. If not authenticated, return default structure
        console.log('🔄 User not authenticated, using default structure...');
        const defaultData = this.getDefaultStructure();
        this.isInitialized = true;
        this.offlineData = defaultData;
        return defaultData;
      }
    } catch (error) {
      console.error('❌ Error in ultra-fast init:', error);
      return this.getDefaultStructure();
    }
  }

  // Load only the absolute minimum needed to show the UI
  async loadMinimalEssential() {
    try {
      // Load all clinical structure data from Firebase
      const [
        organizations,
        locations,
        buildings,
        floors,
        departments,
        rooms,
        beds,
        equipment,
        personnel,
        biological_entities,
        users
      ] = await Promise.all([
        organizationService.getAll(),
        locationService.getAll(),
        buildingService.getAll(),
        floorService.getAll(),
        departmentService.getAll(),
        roomService.getAll(),
        bedService.getAll(),
        equipmentService.getAll(),
        personnelService.getAll(),
        biologicalEntityService.getAll(),
        userService.getAll()
      ]);
      
      const structure = {
        company: organizations[0] || this.getDefaultCompany(),
        organizations: organizations || [],
        locations: locations || [],
        buildings: buildings || [],
        floors: floors || [],
        departments: departments || [],
        rooms: rooms || [],
        beds: beds || [],
        equipment: equipment || [],
        personnel: personnel || [],
        biological_entities: biological_entities || [],
        users: users || [],
        // Static data (no Firebase needed)
        icLeadership: this.getDefaultICLeadership(),
        riskManagement: this.getDefaultRiskManagement(),
        policyFramework: this.getDefaultPolicyFramework(),
        haiTargetRates: this.getDefaultHAITargetRates(),
        complianceTargets: this.getDefaultComplianceTargets(),
        qualityIndicators: this.getDefaultQualityIndicators(),
        patientSafetyGoals: this.getDefaultPatientSafetyGoals(),
        departmentTypes: this.getDefaultDepartmentTypes(),
        roomTypes: this.getDefaultRoomTypes(),
        bedStates: this.getDefaultBedStates(),
        equipmentTypes: this.getDefaultEquipmentTypes()
      };

      console.log('✅ All clinical data loaded from Firebase');
      return structure;
    } catch (error) {
      console.error('❌ Error loading clinical data:', error);
      return this.getDefaultStructure();
    }
  }

  // Background sync - refresh all data from Firebase
  async syncInBackground() {
    try {
      console.log('🔄 Background sync started...');
      
      // Refresh all collections from Firebase
      const [
        organizations,
        locations,
        buildings,
        floors,
        departments,
        rooms,
        beds,
        equipment,
        personnel,
        biological_entities,
        users
      ] = await Promise.all([
        this.getCachedOrFetch('organizations', () => organizationService.getAll()),
        this.getCachedOrFetch('locations', () => locationService.getAll()),
        this.getCachedOrFetch('buildings', () => buildingService.getAll()),
        this.getCachedOrFetch('floors', () => floorService.getAll()),
        this.getCachedOrFetch('departments', () => departmentService.getAll()),
        this.getCachedOrFetch('rooms', () => roomService.getAll()),
        this.getCachedOrFetch('beds', () => bedService.getAll()),
        this.getCachedOrFetch('equipment', () => equipmentService.getAll()),
        this.getCachedOrFetch('personnel', () => personnelService.getAll()),
        this.getCachedOrFetch('biological_entities', () => biologicalEntityService.getAll()),
        this.getCachedOrFetch('users', () => userService.getAll())
      ]);

      // Update the structure with new data
      if (this.offlineData) {
        this.offlineData.organizations = organizations || [];
        this.offlineData.locations = locations || [];
        this.offlineData.buildings = buildings || [];
        this.offlineData.floors = floors || [];
        this.offlineData.departments = departments || [];
        this.offlineData.rooms = rooms || [];
        this.offlineData.beds = beds || [];
        this.offlineData.equipment = equipment || [];
        this.offlineData.personnel = personnel || [];
        this.offlineData.biological_entities = biological_entities || [];
        this.offlineData.users = users || [];
        
        // Save updated cache
        this.saveToLocalStorage(this.offlineData);
        this.lastSyncTime = Date.now();
        
        console.log('✅ Background sync completed for all collections');
      }
    } catch (error) {
      console.error('❌ Background sync failed:', error);
    }
  }

  // Load additional data on demand with aggressive caching
  async loadOnDemand(collectionType) {
    const cacheKey = `on-demand-${collectionType}`;
    
    // Check memory cache first
    if (this.cache.has(cacheKey)) {
      console.log(`⚡ ${collectionType} loaded from memory cache`);
      return this.cache.get(cacheKey);
    }
    
    // Check localStorage cache
    const localCacheKey = `cache_${collectionType}`;
    const cachedData = localStorage.getItem(localCacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        if (this.isCacheValid(parsed, 5 * 60 * 1000)) { // 5 minutes cache
          console.log(`⚡ ${collectionType} loaded from localStorage cache`);
          this.cache.set(cacheKey, parsed.data);
          return parsed.data;
        }
      } catch (e) {
        // Invalid cache, continue to fetch
      }
    }
    
    // Fetch from Firebase
    console.log(`🔄 Fetching ${collectionType} from Firebase...`);
    try {
      const service = this.getServiceForCollection(collectionType);
      const data = await service.getAll();
      
      // Cache in memory and localStorage
      this.cache.set(cacheKey, data);
      localStorage.setItem(localCacheKey, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
      
      console.log(`✅ ${collectionType} loaded and cached`);
      return data;
    } catch (error) {
      console.error(`❌ Error loading ${collectionType}:`, error);
      return [];
    }
  }

  // Memory-only cache - NO localStorage to avoid quota issues
  saveToLocalStorage(data) {
    // DISABLED: localStorage is too small for clinical data
    // Instead, we use memory-only caching which is much faster
    // and doesn't have quota issues
    
    // Store in memory cache instead
    this.offlineData = {
      data,
      timestamp: Date.now()
    };
    
    console.log('💾 Data cached in memory (localStorage disabled to avoid quota issues)');
  }

  loadFromLocalStorage() {
    // DISABLED: localStorage is too small for clinical data
    // Use memory cache instead
    
    if (this.offlineData && this.offlineData.data) {
      console.log('📦 Loading from memory cache');
      return this.offlineData.data;
    }
    
    console.log('ℹ️ No memory cache available');
    return null;
  }

  isCacheValid(cachedData, maxAge = 30 * 60 * 1000) { // 30 minutes default
    if (!cachedData || !cachedData.timestamp) return false;
    
    const age = Date.now() - cachedData.timestamp;
    return age < maxAge;
  }

  // Clear all caches
  clearAllCaches() {
    // Clear memory cache
    this.cache.clear();
    this.offlineData = null;
    
    // Clear any localStorage items (though we don't use it anymore)
    try {
      localStorage.removeItem('clinical_data_cache');
      const collections = ['organizations', 'personnel', 'biological_entities', 'locations', 'buildings', 'departments', 'floors', 'rooms', 'beds', 'equipment'];
      collections.forEach(col => {
        localStorage.removeItem(`cache_${col}`);
      });
    } catch (error) {
      // Ignore localStorage errors
    }
    
    console.log('🧹 All caches cleared (memory-only)');
  }

  // Sync when coming back online
  async syncWhenOnline() {
    if (!this.isOnline) return;
    
    try {
      console.log('🔄 Syncing after coming online...');
      await this.syncInBackground();
    } catch (error) {
      console.error('❌ Sync after online failed:', error);
    }
  }

  // Save operations with offline support
  async saveWithOfflineSupport(collectionName, items, options = {}) {
    const { throwOnError = false } = options;
    try {
      if (this.isOnline) {
        await this.saveCollection(collectionName, items);
      } else {
        this.queueForSync(collectionName, items);
      }
      this.updateLocalCache(collectionName, items);
    } catch (error) {
      console.error(`❌ Error saving ${collectionName}:`, error);
      this.updateLocalCache(collectionName, items);
      if (throwOnError) {
        throw error;
      }
    }
  }

  queueForSync(collectionName, items) {
    try {
      const queue = JSON.parse(localStorage.getItem('sync_queue') || '[]');
      queue.push({
        collection: collectionName,
        items,
        timestamp: Date.now()
      });
      localStorage.setItem('sync_queue', JSON.stringify(queue));
      console.log(`📝 Queued ${collectionName} for sync when online`);
    } catch (error) {
      console.error('❌ Error queuing for sync:', error);
    }
  }

  updateLocalCache(collectionName, items) {
    try {
      if (this.offlineData) {
        this.offlineData[collectionName] = items;
        this.saveToLocalStorage(this.offlineData);
      }
    } catch (error) {
      console.error('❌ Error updating local cache:', error);
    }
  }

  // Helper methods (copied from optimized service)
  async getCachedOrFetch(key, fetchFn) {
    if (this.cache.has(key)) {
      return this.cache.get(key);
    }
    
    const data = await fetchFn();
    this.cache.set(key, data);
    return data;
  }

  getServiceForCollection(collectionName) {
    const services = {
      organizations: organizationService,
      locations: locationService,
      buildings: buildingService,
      floors: floorService,
      departments: departmentService,
      rooms: roomService,
      beds: bedService,
      equipment: equipmentService,
      personnel: personnelService,
      biological_entities: biologicalEntityService,
      users: userService
    };
    return services[collectionName];
  }

  async saveCollection(collectionName, items) {
    try {
      const service = this.getServiceForCollection(collectionName);
      if (!service) {
        throw new Error(`Unknown collection: ${collectionName}`);
      }

      for (const item of items || []) {
        if (item.id) {
          await service.update(item.id, item);
        } else {
          await service.create(item);
        }
      }

      console.log(`✅ ${collectionName} saved to Firebase`);
    } catch (error) {
      console.error(`❌ Error saving ${collectionName}:`, error);
      throw error;
    }
  }

  getDefaultCompany() {
    console.debug('ℹ️ No organization data cached — will load from Firebase after authentication.');
    return null;
  }

  getDefaultICLeadership() {
    console.debug('ℹ️ No IC leadership data cached — will load from Firebase after authentication.');
    return null;
  }

  getDefaultRiskManagement() {
    return {
      riskAssessmentFrequency: 'Monthly',
      riskRegister: [
        { id: 'risk-1', description: 'HAI Transmission', probability: 'Medium', impact: 'High', mitigation: 'Enhanced IPC measures' },
        { id: 'risk-2', description: 'Antibiotic Resistance', probability: 'High', impact: 'Critical', mitigation: 'Antibiotic stewardship program' }
      ],
      mitigationStrategies: ['Hand Hygiene', 'Environmental Cleaning', 'Antibiotic Stewardship'],
      monitoringFrequency: 'Daily',
      reportingStructure: 'Weekly reports to management',
      escalationProcedures: ['Immediate notification for critical risks', 'Monthly risk committee meetings']
    };
  }

  getDefaultPolicyFramework() {
    return {
      icPolicies: [
        'Politica de Igienă a Mâinilor',
        'Politica de Izolare a Pacienților',
        'Politica de Curățare și Dezinfectare',
        'Politica de Managementul Antibioticelor'
      ],
      trainingPrograms: [
        'Formare IPC pentru Personal Medical',
        'Formare IPC pentru Personal Auxiliar',
        'Formare IPC pentru Studenți'
      ],
      emergencyProtocols: [
        'Protocol de Urgență pentru Broaște de Infecții',
        'Protocol de Evacuare în Caz de Incendiu',
        'Protocol de Managementul Dezastrelor'
      ]
    };
  }

  getDefaultHAITargetRates() {
    return {
      cdi: '3.2/1000 patient-days',
      cauti: '2.1/1000 catheter-days',
      clabsi: '1.8/1000 line-days',
      ssi: '2.5%',
      vap: '1.2/1000 ventilator-days',
      handHygieneTarget: '95%',
      cleaningComplianceTarget: '98%',
      educationComplianceTarget: '100%'
    };
  }

  getDefaultComplianceTargets() {
    return {
      handHygiene: '95%',
      cleaningCompliance: '98%',
      educationCompliance: '100%',
      isolationCompliance: '100%',
      antibioticStewardship: '90%'
    };
  }

  getDefaultQualityIndicators() {
    return [
      'Rata de Infecții Nosocomiale',
      'Compliance Igiena Mâinilor',
      'Satisfacția Pacienților',
      'Timpul de Răspuns la Urgențe'
    ];
  }

  getDefaultPatientSafetyGoals() {
    return [
      'Reducerea infecțiilor nosocomiale cu 20%',
      'Îmbunătățirea compliance-ului la igiena mâinilor la 95%',
      'Reducerea rezistenței la antibiotice cu 15%'
    ];
  }

  getDefaultDepartmentTypes() {
    return ['Cardiologie', 'Neurologie', 'Chirurgie', 'Pediatrie', 'Terapie Intensivă', 'Urgențe'];
  }

  getDefaultRoomTypes() {
    return ['Camera de Pacient', 'Sala de Operații', 'Sala de Consultații', 'Camera de Terapie Intensivă', 'Depozit'];
  }

  getDefaultBedStates() {
    return ['Disponibil', 'Ocupat', 'Curățare', 'Întreținere', 'Rezervat'];
  }

  getDefaultEquipmentTypes() {
    return ['Scanner RMN', 'Scanner CT', 'Radiografie', 'Ventilator', 'Monitor Pacient', 'Ecograf'];
  }

  getDefaultStructure() {
    return {
      company: this.getDefaultCompany(),
      organizations: [],
      locations: [],
      buildings: [],
      floors: [],
      departments: [],
      rooms: [],
      beds: [],
      equipment: [],
      personnel: [],
      biological_entities: [],
      icLeadership: this.getDefaultICLeadership(),
      riskManagement: this.getDefaultRiskManagement(),
      policyFramework: this.getDefaultPolicyFramework(),
      haiTargetRates: this.getDefaultHAITargetRates(),
      complianceTargets: this.getDefaultComplianceTargets(),
      qualityIndicators: this.getDefaultQualityIndicators(),
      patientSafetyGoals: this.getDefaultPatientSafetyGoals(),
      departmentTypes: this.getDefaultDepartmentTypes(),
      roomTypes: this.getDefaultRoomTypes(),
      bedStates: this.getDefaultBedStates(),
      equipmentTypes: this.getDefaultEquipmentTypes()
    };
  }
}

// Export singleton instance
export const productionOptimizedService = new ProductionOptimizedService();
